import { useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useProduct = (productId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["product", scope.merchantId, scope.workspaceId, productId],
    queryFn: async () => {
      if (!productId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].product[":productId"].$get({
            param: {
              ...scope.param,
              productId,
            },
          })
        : await client.merchant[":merchantId"].product[":productId"].$get({
            param: {
              ...scope.param,
              productId,
            },
          });

      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }

      const productData = await res.json();

      return productData;
    },
    enabled: !!scope.merchantId && !!productId,
  });
};

export const useProductRelatedResources = (productId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["product", "resources", scope.merchantId, scope.workspaceId, productId],
    queryFn: async () => {
      if (!productId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].product[":productId"].resources.$get({
            param: {
              ...scope.param,
              productId,
            },
          })
        : await client.merchant[":merchantId"].product[":productId"].resources.$get({
            param: {
              ...scope.param,
              productId,
            },
          });

      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }

      const productData = await res.json();

      return productData;
    },
    enabled: !!scope.merchantId && !!productId,
  });
};

export const useProductDailyMetrics = (productId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["product", "daily-metrics", scope.merchantId, scope.workspaceId, productId],
    queryFn: async () => {
      if (!productId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].product[":productId"]["daily-metrics"].$get({
            param: {
              ...scope.param,
              productId,
            },
          })
        : await client.merchant[":merchantId"].product[":productId"]["daily-metrics"].$get({
            param: {
              ...scope.param,
              productId,
            },
          });

      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }

      const dailyMetrics = await res.json();

      return dailyMetrics;
    },
    enabled: !!scope.merchantId && !!productId,
  });
};

export const useProductContentAnalysis = (productId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["product", "content-analysis", scope.merchantId, scope.workspaceId, productId],
    queryFn: async () => {
      if (!productId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].product[":productId"]["content-analysis"].$get(
            {
              param: {
                ...scope.param,
                productId,
              },
            },
          )
        : await client.merchant[":merchantId"].product[":productId"]["content-analysis"].$get({
            param: {
              ...scope.param,
              productId,
            },
          });

      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }

      const contentAnalysis = await res.json();

      return contentAnalysis;
    },
    enabled: !!scope.merchantId && !!productId,
  });
};

export const useProductSafetyAnalysis = (productId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["product", "safety-analysis", scope.merchantId, scope.workspaceId, productId],
    queryFn: async () => {
      if (!productId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].product[":productId"]["safety-analysis"].$get({
            param: {
              ...scope.param,
              productId,
            },
          })
        : await client.merchant[":merchantId"].product[":productId"]["safety-analysis"].$get({
            param: {
              ...scope.param,
              productId,
            },
          });

      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId && !!productId,
  });
};
